/**
 * Helpers comunes para secciones.
 */
import { apiClient } from '../lib/api.js';

export function h(tag, attrs = {}, children = []) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs || {})) {
    if (k === 'class') el.className = v;
    else if (k === 'style') el.style.cssText = v;
    else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v);
    else if (k === 'html') el.innerHTML = v;
    else if (v != null) el.setAttribute(k, v);
  }
  for (const c of [].concat(children)) {
    if (c == null) continue;
    el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return el;
}

export function card(title, opts = {}) {
  const { subtitle, actions, accent } = opts;
  return h('section', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-5 mb-4' + (accent ? ' border-gold-400/30' : '') }, [
    h('div', { class: 'flex items-start justify-between mb-3' }, [
      h('div', {}, [
        h('h3', { class: 'font-display font-semibold text-base text-ink-100' }, title),
        subtitle ? h('p', { class: 'text-xs text-ink-300 mt-0.5' }, subtitle) : null,
      ]),
      actions ?? null,
    ]),
  ]);
}

export function btn(label, onclickOrOpts, opts = {}) {
  // Backwards-compat: acepta btn(label, fn, opts) Y btn(label, { onclick, class, variant })
  let onclick;
  if (typeof onclickOrOpts === 'function') {
    onclick = onclickOrOpts;
  } else if (onclickOrOpts && typeof onclickOrOpts === 'object') {
    onclick = onclickOrOpts.onclick;
    opts = { ...onclickOrOpts, ...opts };
  }
  const variant = opts.variant || 'primary';
  const defaultCls = variant === 'primary'
    ? 'bg-gradient-to-r from-gold-400 to-gold-500 hover:from-gold-300 hover:to-gold-400 text-base-900 font-semibold px-4 py-2 rounded-lg text-sm transition-all'
    : 'bg-base-700 hover:bg-base-600 text-ink-100 border border-base-600 px-4 py-2 rounded-lg text-sm transition-all';
  const cls = opts.class || defaultCls;
  return h('button', { class: cls, onclick }, label);
}

export function pill(text, color = 'gold') {
  const colors = {
    gold: 'bg-gold-400/15 text-gold-300 border-gold-400/30',
    green: 'bg-emerald-400/15 text-emerald-300 border-emerald-400/30',
    red: 'bg-red-400/15 text-red-300 border-red-400/30',
    blue: 'bg-sky-400/15 text-sky-300 border-sky-400/30',
    gray: 'bg-base-600 text-ink-200 border-base-600',
  };
  return h('span', { class: `inline-flex items-center text-xs px-2 py-0.5 rounded-full border ${colors[color] || colors.gold}` }, text);
}

export function emptyState(emoji, title, subtitle, action) {
  return h('div', { class: 'text-center py-12 bg-base-800/30 border border-dashed border-base-700 rounded-xl' }, [
    h('div', { class: 'text-5xl mb-3 grayscale opacity-80' }, emoji),
    h('h3', { class: 'font-display font-semibold text-lg' }, title),
    h('p', { class: 'text-sm text-ink-300 mt-1 max-w-md mx-auto' }, subtitle || ''),
    action ? h('div', { class: 'mt-4' }, action) : null,
  ]);
}

export function skeleton(opts = {}) {
  const { width = '100%', height = '1rem', rounded = 'rounded' } = opts;
  return h('div', { class: `bg-base-700/50 ${rounded} animate-pulse`, style: `width: ${width}; height: ${height}` });
}

export function skeletonCard(lines = 3) {
  const children = [];
  for (let i = 0; i < lines; i++) {
    children.push(skeleton({ width: i === 0 ? '60%' : i === lines - 1 ? '40%' : '90%', height: '0.75rem' }));
  }
  return h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-5 space-y-3' }, children);
}

export function skeletonGrid(count = 4, cols = 4) {
  const colsCls = { 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-4', 5: 'md:grid-cols-5' };
  const items = [];
  for (let i = 0; i < count; i++) items.push(skeletonCard(3));
  return h('div', { class: `grid grid-cols-2 ${colsCls[cols] || colsCls[4]} gap-3` }, items);
}

export function formatEur(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

export function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Modal universal — reemplaza prompt() y alert() nativos.
 */
export function openModal({ title, subtitle, fields = [], onSubmit, submitLabel = 'Guardar', cancelLabel = 'Cancelar', size = 'md', icon }) {
  return new Promise((resolve) => {
    const sizes = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-2xl', xl: 'max-w-4xl' };
    const overlay = h('div', {
      class: 'fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in',
      onclick: (e) => { if (e.target === overlay) { overlay.remove(); resolve(null); } },
    });
    const modal = h('div', { class: `bg-base-900 border border-base-700 rounded-2xl ${sizes[size] || sizes.md} w-full max-h-[90vh] overflow-y-auto shadow-2xl` });
    const header = h('div', { class: 'p-5 border-b border-base-700 flex items-start justify-between sticky top-0 bg-base-900 z-10' }, [
      h('div', { class: 'flex items-start gap-3' }, [
        icon ? h('div', { class: 'text-3xl' }, icon) : null,
        h('div', {}, [
          h('h3', { class: 'font-display font-bold text-lg text-ink-100' }, title),
          subtitle ? h('p', { class: 'text-xs text-ink-400 mt-1' }, subtitle) : null,
        ]),
      ]),
      h('button', { class: 'text-ink-400 hover:text-ink-100 text-2xl leading-none transition', onclick: () => { overlay.remove(); resolve(null); } }, '×'),
    ]);
    modal.appendChild(header);
    const form = h('form', {
      class: 'p-5 space-y-4',
      onsubmit: async (e) => {
        e.preventDefault();
        const values = {};
        for (const f of fields) {
          const el = form.querySelector(`[name="${f.name}"]`);
          if (!el) continue;
          values[f.name] = f.type === 'checkbox' ? el.checked : el.value;
        }
        const submitBtn = form.querySelector('button[type=submit]');
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Guardando...'; }
        try {
          await onSubmit(values);
          overlay.remove();
          resolve(values);
        } catch (err) {
          let errBox = form.querySelector('.modal-error');
          if (!errBox) {
            errBox = h('div', { class: 'modal-error bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg p-3' });
            form.insertBefore(errBox, form.firstChild);
          }
          errBox.textContent = err?.message || 'Error al guardar';
          if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = submitLabel; }
        }
      },
    });
    fields.forEach((f) => form.appendChild(renderField(f)));
    form.appendChild(h('div', { class: 'flex gap-2 justify-end pt-3 border-t border-base-700' }, [
      h('button', { type: 'button', class: 'bg-base-700 hover:bg-base-600 text-ink-100 px-4 py-2 rounded-lg text-sm transition', onclick: () => { overlay.remove(); resolve(null); } }, cancelLabel),
      h('button', { type: 'submit', class: 'bg-gradient-to-r from-gold-400 to-gold-500 hover:from-gold-300 hover:to-gold-400 text-base-900 font-semibold px-5 py-2 rounded-lg text-sm transition shadow-lg shadow-gold-500/20' }, submitLabel),
    ]));
    modal.appendChild(form);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    setTimeout(() => { const first = form.querySelector('input, textarea, select'); if (first) first.focus(); }, 50);
  });
}

function renderField(f) {
  const baseInput = 'w-full bg-base-800 border border-base-700 focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30 outline-none rounded-lg px-3 py-2 text-sm transition';
  // Tipo 'html' = bloque de solo-lectura con HTML preformateado (vista detalle)
  if (f.type === 'html') {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = f.value || '';
    const hidden = h('input', { type: 'hidden', name: f.name, value: '' });
    return h('div', {}, [hidden, wrapper]);
  }
  let input;
  if (f.type === 'textarea') {
    input = h('textarea', { name: f.name, class: baseInput + ' resize-y', rows: f.rows || 4, placeholder: f.placeholder || '', required: f.required }, f.value || '');
  } else if (f.type === 'select') {
    input = h('select', { name: f.name, class: baseInput, required: f.required },
      (f.options || []).map((o) => {
        const opt = document.createElement('option');
        opt.value = typeof o === 'string' ? o : o.value;
        opt.textContent = typeof o === 'string' ? o : o.label;
        if (f.value === opt.value) opt.selected = true;
        return opt;
      }));
  } else if (f.type === 'checkbox') {
    const cb = h('input', { name: f.name, type: 'checkbox', class: 'rounded border-base-600' });
    if (f.value) cb.checked = true;
    input = h('label', { class: 'flex items-center gap-2 text-sm text-ink-200 cursor-pointer' }, [
      cb, h('span', {}, f.checkboxLabel || f.label),
    ]);
    return h('div', {}, input);
  } else {
    input = h('input', { name: f.name, type: f.type || 'text', class: baseInput, placeholder: f.placeholder || '', value: f.value || '', required: f.required });
  }
  return h('div', {}, [
    h('label', { class: 'block text-xs font-semibold text-ink-300 uppercase tracking-wide mb-1' }, f.label),
    input,
    f.help ? h('p', { class: 'text-xs text-ink-400 mt-1' }, f.help) : null,
  ]);
}

export function openConfirm({ title = '¿Estás seguro?', message = '', confirmLabel = 'Confirmar', cancelLabel = 'Cancelar', danger = false, icon = '⚠️' }) {
  return new Promise((resolve) => {
    const overlay = h('div', {
      class: 'fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in',
      onclick: (e) => { if (e.target === overlay) { overlay.remove(); resolve(false); } },
    }, [
      h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl max-w-sm w-full p-6' }, [
        h('div', { class: 'text-4xl mb-3 text-center' }, icon),
        h('h3', { class: 'font-display font-bold text-lg text-center mb-2' }, title),
        message ? h('p', { class: 'text-sm text-ink-300 text-center mb-5' }, message) : null,
        h('div', { class: 'flex gap-2' }, [
          h('button', { class: 'flex-1 bg-base-700 hover:bg-base-600 text-ink-100 px-4 py-2 rounded-lg text-sm transition', onclick: () => { overlay.remove(); resolve(false); } }, cancelLabel),
          h('button', {
            class: `flex-1 ${danger ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-gradient-to-r from-gold-400 to-gold-500 text-base-900'} font-semibold px-4 py-2 rounded-lg text-sm transition`,
            onclick: () => { overlay.remove(); resolve(true); },
          }, confirmLabel),
        ]),
      ]),
    ]);
    document.body.appendChild(overlay);
  });
}

export function toast(message, type = 'success', duration = 3000) {
  const colors = {
    success: 'bg-emerald-500/95 text-white',
    error: 'bg-red-500/95 text-white',
    info: 'bg-sky-500/95 text-white',
    warn: 'bg-amber-500/95 text-base-900',
  };
  const icons = { success: '✓', error: '✕', info: 'ℹ', warn: '⚠' };
  let container = document.getElementById('toast-container');
  if (!container) {
    container = h('div', { id: 'toast-container', class: 'fixed top-4 right-4 z-[60] flex flex-col gap-2 pointer-events-none' });
    document.body.appendChild(container);
  }
  const t = h('div', {
    class: `${colors[type] || colors.info} shadow-xl rounded-lg px-4 py-3 text-sm flex items-center gap-3 min-w-[200px] max-w-md pointer-events-auto`,
  }, [
    h('span', { class: 'text-lg font-bold' }, icons[type] || icons.info),
    h('span', {}, message),
  ]);
  container.appendChild(t);
  setTimeout(() => {
    t.style.transition = 'opacity 0.3s, transform 0.3s';
    t.style.opacity = '0';
    t.style.transform = 'translateX(20px)';
    setTimeout(() => t.remove(), 300);
  }, duration);
}

export function statTile({ label, value, delta, emoji, color = 'gold', big = false }) {
  const colors = {
    gold: 'border-gold-400/30 bg-gradient-to-br from-gold-500/10 to-transparent',
    green: 'border-emerald-400/30 bg-gradient-to-br from-emerald-500/10 to-transparent',
    red: 'border-red-400/30 bg-gradient-to-br from-red-500/10 to-transparent',
    blue: 'border-sky-400/30 bg-gradient-to-br from-sky-500/10 to-transparent',
    purple: 'border-purple-400/30 bg-gradient-to-br from-purple-500/10 to-transparent',
    gray: 'border-base-700',
  };
  const valColors = { gold: 'text-gold-300', green: 'text-emerald-300', red: 'text-red-300', blue: 'text-sky-300', purple: 'text-purple-300', gray: 'text-ink-100' };
  return h('div', { class: `bg-base-800/40 border ${colors[color] || colors.gold} rounded-xl p-4 transition hover:scale-[1.02]` }, [
    h('div', { class: 'flex items-center justify-between mb-1' }, [
      h('span', { class: 'text-[10px] uppercase tracking-wider text-ink-400 font-semibold' }, label),
      emoji ? h('span', { class: 'text-lg' }, emoji) : null,
    ]),
    h('div', { class: `${big ? 'text-3xl' : 'text-2xl'} font-display font-bold ${valColors[color] || 'text-ink-100'}` }, String(value)),
    delta != null ? h('div', { class: `text-xs mt-1 ${Number(delta) >= 0 ? 'text-emerald-400' : 'text-red-400'}` }, `${Number(delta) >= 0 ? '↗' : '↘'} ${delta}`) : null,
  ]);
}

/**
 * Quick task creator — modal compacto para añadir una tarea desde cualquier sección.
 * @param {string} seccion - 'personal'|'autosell'|'inmuebles'|'carlos-ai'|'coco-doree'|'suiza-go'|'salud'
 * @param {Function} [onDone] - callback tras crear
 */
export function openQuickTaskModal(seccion = 'personal', onDone) {
  return openModal({
    title: 'Nueva tarea',
    icon: '✅',
    subtitle: `Sección: ${seccion}`,
    fields: [
      { name: 'title', label: 'Título', required: true, placeholder: '¿Qué tienes que hacer?' },
      { name: 'priority', label: 'Prioridad', type: 'select', value: 'media', options: [
        { value: 'critica', label: 'P0 — Urgente' },
        { value: 'alta', label: 'P1 — Alta' },
        { value: 'media', label: 'P2 — Media' },
        { value: 'baja', label: 'P3 — Baja' },
      ] },
      { name: 'hasSchedule', label: '¿Tiene horario específico?', type: 'checkbox', checkboxLabel: 'Sí, tiene hora/fecha concreta' },
      { name: 'startAt', label: 'Fecha y hora inicio', type: 'datetime-local', help: 'Solo si marcaste "tiene horario específico"' },
      { name: 'description', label: 'Notas (opcional)', type: 'textarea', rows: 3 },
    ],
    onSubmit: async (vals) => {
      const payload = {
        title: vals.title,
        seccion,
        priority: vals.priority,
        description: vals.description || null,
        status: 'todo',
      };
      if (vals.hasSchedule && vals.startAt) {
        payload.startAt = new Date(vals.startAt).getTime();
      }
      await apiClient.post('/api/v1/tasks', payload);
      toast('Tarea creada', 'success');
      if (typeof onDone === 'function') onDone();
    },
  });
}

export function sectionHeader(emoji, title, subtitle, actions) {
  return h('div', { class: 'flex items-start justify-between flex-wrap gap-3 mb-6' }, [
    h('div', { class: 'flex items-center gap-3' }, [
      h('div', { class: 'w-12 h-12 rounded-2xl bg-gradient-to-br from-gold-400/20 to-gold-600/10 border border-gold-400/30 flex items-center justify-center text-2xl' }, emoji),
      h('div', {}, [
        h('h2', { class: 'font-display text-2xl md:text-3xl font-bold' }, title),
        subtitle ? h('p', { class: 'text-sm text-ink-300' }, subtitle) : null,
      ]),
    ]),
    actions ? h('div', { class: 'flex gap-2' }, actions) : null,
  ]);
}

export { apiClient };
